/** Viewport-fitted dashboard: compact metrics (scroll) + panels (scroll) within main area. */

export function DashboardPage({ children, className = '' }) {
  return (
    <div className={`flex flex-col h-full min-h-0 gap-2 overflow-hidden ${className}`.trim()}>
      {children}
    </div>
  );
}

export function DashboardHeader({ title, subtitle, titleBadge, action }) {
  return (
    <header className="flex-shrink-0 pb-0 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-lg font-semibold text-[hsl(var(--ds-fg))] leading-tight">{title}</h1>
          {titleBadge}
        </div>
        {subtitle && <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-0.5">{subtitle}</p>}
      </div>
      {action ? <div className="shrink-0 pt-0.5">{action}</div> : null}
    </header>
  );
}

/** Badge toggle between operational dashboard and analytics insights view. */
export function DashboardInsightsToggle({ active, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      title={active ? 'Back to dashboard overview' : 'Open hiring analytics & insights'}
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border transition-all shadow-sm ${
        active
          ? 'bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] border-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] ring-2 ring-[hsl(var(--ds-ring))]'
          : 'bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-secondary))] border-[hsl(var(--ds-secondary))] hover:bg-[hsl(var(--ds-secondary)/0.08)]'
      }`}
    >
      <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
        <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zm6-4a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zm6-3a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
      </svg>
      <span className="text-[11px] font-bold tracking-wide uppercase">
        {active ? 'Overview' : 'Insights'}
      </span>
    </button>
  );
}

/** Full-height scroll area for the expanded analytics insights view. */
export function DashboardInsightsBody({ children }) {
  return (
    <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pr-1 -mr-1 space-y-4 pb-2">
      {children}
    </div>
  );
}

/** Responsive grid for insight panels — no viewport row cap. */
export function DashboardInsightsPanelsGrid({ children, columns = 2 }) {
  const cols =
    columns === 3
      ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'
      : 'grid-cols-1 md:grid-cols-2';
  return <div className={`grid ${cols} gap-3`}>{children}</div>;
}

export function DashboardSectionTitle({ children, action }) {
  return (
    <div className="flex items-center justify-between gap-2 mb-2">
      <div className="text-[11px] font-semibold text-[hsl(var(--ds-muted-fg))] uppercase tracking-wide">
        {children}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

/** All metric cards — scrolls internally if needed, never grows past ~32% viewport */
export function DashboardMetrics({ children, expanded = false }) {
  return (
    <div
      className={`flex-shrink-0 overflow-y-auto overflow-x-hidden pr-1 -mr-1 ${
        expanded ? 'max-h-[min(42vh,16rem)]' : 'max-h-[min(32vh,11rem)]'
      }`}
    >
      <div className="space-y-2">{children}</div>
    </div>
  );
}

/** Responsive grid for stat cards */
export function DashboardMetricGrid({ children, className = '' }) {
  return (
    <div
      className={`grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7 xl:grid-cols-8 gap-2 ${className}`}
    >
      {children}
    </div>
  );
}

/** Panels fill remaining height; each panel scrolls inside */
export function DashboardPanelsGrid({ children, columns = 2 }) {
  const cols = columns === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2';
  const rows = columns === 1 ? '' : 'auto-rows-fr md:grid-rows-2';
  return (
    <div className={`flex-1 min-h-0 grid ${cols} ${rows} gap-3 overflow-hidden`}>
      {children}
    </div>
  );
}

export function DashboardPanel({ title, subtitle, action, children, className = '' }) {
  return (
    <section
      className={`flex flex-col min-h-0 bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] rounded-xl overflow-hidden shadow-[var(--ds-shadow-card)] ${className}`}
    >
      {title && (
        <div className="flex-shrink-0 px-3 py-2 border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-[hsl(var(--ds-fg))]">{title}</h2>
            {subtitle && <p className="text-[10px] text-[hsl(var(--ds-muted-fg))] mt-0.5">{subtitle}</p>}
          </div>
          {action ? <div className="shrink-0">{action}</div> : null}
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-3 bg-[hsl(var(--ds-surface))]">{children}</div>
    </section>
  );
}
